import React, { useEffect, useMemo, useState } from "react";
import { api, isoDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Minus, Save, AlertCircle, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function YesNo({ value, onChange, testid }) {
  return (
    <div className="flex gap-2" data-testid={testid}>
      {["Y","N"].map(v => (
        <Button key={v} size="sm" variant={value === v ? "default" : "outline"} onClick={() => onChange(v)}
          className={value === v ? "gold-bg hover:opacity-90" : "border-navy-700 text-muted-foreground"}>
          {v === "Y" ? "Yes" : "No"}
        </Button>
      ))}
    </div>
  );
}

function Counter({ label, value, onChange, max = 20, min = 0, hint, testid, error }) {
  const num = Number(value) || 0;
  return (
    <div data-testid={testid}>
      <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-9 w-9 border-navy-700" onClick={() => onChange(Math.max(min, num - 1))} type="button"><Minus className="h-3.5 w-3.5" /></Button>
        <Input type="number" value={num} onChange={e => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))} className={`w-20 text-center bg-navy-800 border-navy-700 num ${error ? "border-rose_alert" : ""}`} min={min} max={max} />
        <Button size="sm" variant="outline" className="h-9 w-9 border-navy-700" onClick={() => onChange(Math.min(max, num + 1))} type="button"><Plus className="h-3.5 w-3.5" /></Button>
      </div>
      {hint && !error && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
      {error && <p className="text-[11px] text-rose_alert mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {error}</p>}
    </div>
  );
}

const CROSSSELL_OPTIONS = ["Botox","Fillers","PRP","Hair Transplant","Laser","HydraFacial","Skin Treatment","Hair Treatment","Other"];
const NOT_TAKEN_REASONS = ["Price","Needs Time to Decide","Family Discussion","Not Ready","Wants Comparison","Wants Second Opinion","Appointment Later","Medical/Clinical Reason","Other"];

export default function DailyTracker() {
  const { user, doctor } = useAuth();
  const isAdmin = ["founder","manager"].includes(user?.role);
  const [doctors, setDoctors] = useState([]);
  const [docId, setDocId] = useState(null);
  const [date, setDate] = useState(isoDate(new Date()));
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null); // { log_id, last_edited_by, created_by }
  const [history, setHistory] = useState(null); // opened history payload

  useEffect(() => {
    const q = isAdmin ? "?status=active" : "";
    api.get(`/doctors${q}`).then(r => {
      setDoctors(r.data);
      if (user?.role === "doctor" && doctor) setDocId(doctor.doctor_id);
      else if (r.data[0]) setDocId(r.data[0].doctor_id);
    });
  }, [user, doctor, isAdmin]);

  useEffect(() => {
    if (!docId) return;
    api.get(`/daily-logs?doctor_id=${docId}&date_from=${date}&date_to=${date}`).then(r => {
      const found = r.data[0];
      setData(found ? found.data : {});
      setMeta(found ? { log_id: found.log_id, last_edited_by: found.last_edited_by, created_by: found.created_by, updated_at: found.updated_at } : null);
    });
  }, [docId, date]);

  const currentDoc = doctors.find(d => d.doctor_id === docId) || doctor;
  const isProcedureDoc = ["procedure","both"].includes(currentDoc?.doctor_type || "");

  const intent = Number(data.treatment_intent || 0);
  const taken = Number(data.treatment_taken || 0);
  const notTaken = Math.max(0, intent - taken);
  const totalCons = Number(data.total_consultations || 0);

  const errors = useMemo(() => {
    const e = {};
    if (intent > totalCons && totalCons > 0) e.treatment_intent = "Treatment intent cannot exceed total consultations";
    if (taken > intent) e.treatment_taken = "Treatment taken cannot exceed treatment intent";
    return e;
  }, [intent, taken, totalCons]);

  const conversion = intent > 0 ? Math.round((taken / intent) * 10000) / 100 : null;
  const consConversion = totalCons > 0 ? Math.round((taken / totalCons) * 10000) / 100 : null;

  const complaintRows = (kind, count) => {
    const n = Number(count || 0);
    if (n <= 0) return [];
    const existing = Array.isArray(data[`${kind}_list`]) ? data[`${kind}_list`] : [];
    const arr = [];
    for (let i = 0; i < n; i++) arr.push(existing[i] || { text: "", remark: "", resolved: "N", resolution: "", resolved_date: "" });
    return arr;
  };
  const majorList = complaintRows("major_complaints", data.major_complaints);
  const minorList = complaintRows("minor_complaints", data.minor_complaints);

  const trainedRows = useMemo(() => {
    const n = Number(data.other_doctors_trained || 0);
    if (n <= 0) return [];
    const ex = Array.isArray(data.trained_list) ? data.trained_list : [];
    const arr = [];
    for (let i = 0; i < n; i++) arr.push(ex[i] || { doctor_name: "", topic: "", remark: "" });
    return arr;
  }, [data.other_doctors_trained, data.trained_list]);

  const updateArr = (key, i, field, val) => {
    const src = Array.isArray(data[key]) ? [...data[key]] : [];
    while (src.length <= i) src.push({});
    src[i] = { ...src[i], [field]: val };
    setData(d => ({ ...d, [key]: src }));
  };

  const save = async () => {
    if (!docId) { toast.error("Select a doctor"); return; }
    if (Object.keys(errors).length) { toast.error("Please fix the errors before saving"); return; }
    setSaving(true);
    try {
      const payload = {
        doctor_id: docId, date,
        data: { ...data,
          treatment_not_taken: notTaken,
          treatment_conversion: conversion,
          consultation_conversion: consConversion,
          major_complaints_list: majorList,
          minor_complaints_list: minorList,
          trained_list: trainedRows,
        },
      };
      const res = await api.post("/daily-logs", payload);
      setMeta(res.data ? { log_id: res.data.log_id, last_edited_by: res.data.last_edited_by, created_by: res.data.created_by, updated_at: res.data.updated_at } : null);
      toast.success("Daily tracker saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doctorName = currentDoc?.name || "—";
  const branchName = currentDoc?.branch || "—";

  const openHistory = async () => {
    if (!meta?.log_id) return;
    try {
      const r = await api.get(`/daily-logs/${meta.log_id}/history`);
      setHistory(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load history");
    }
  };
  const fmtWhen = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-6" data-testid="daily-tracker-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Daily Tracker</div>
          <h1 className="mt-2 text-3xl font-heading font-medium">1–3 minutes • Simple • Doctor-centric</h1>
          <div className="gold-underline mt-3" />
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          {isAdmin ? (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Doctor</Label>
              <Select value={docId || ""} onValueChange={setDocId}>
                <SelectTrigger className="w-56 bg-navy-800 border-navy-700" data-testid="tracker-doctor-select"><SelectValue placeholder="Doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name} · {d.branch}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Doctor</Label>
              <div className="mt-1.5 h-10 px-3 rounded-md border border-navy-700 bg-navy-900/60 grid place-items-start content-center" data-testid="tracker-doctor-auto">{doctorName}</div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Branch</Label>
            <div className="mt-1.5 h-10 px-3 rounded-md border border-navy-700 bg-navy-900/60 grid place-items-start content-center text-muted-foreground">{branchName}</div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44 bg-navy-800 border-navy-700" data-testid="tracker-date" />
          </div>
        </div>
      </div>

      {meta && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground" data-testid="audit-chip">
          <span>
            Last edited by <span className="text-slate-200">{meta.last_edited_by?.name || "—"}</span>
            <span className="ml-1 uppercase text-[10px] tracking-widest gold-text">({meta.last_edited_by?.role || "—"})</span>
            <span className="ml-2">on {fmtWhen(meta.updated_at || meta.last_edited_by?.at)}</span>
          </span>
          {meta.created_by && meta.created_by.user_id !== meta.last_edited_by?.user_id && (
            <span>· Created by <span className="text-slate-200">{meta.created_by.name}</span> on {fmtWhen(meta.created_by.at)}</span>
          )}
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-7 border-navy-700 text-xs" onClick={openHistory} data-testid="view-history-btn">
              <History className="h-3 w-3 mr-1" /> View Edit History
            </Button>
          )}
        </div>
      )}

      <Dialog open={!!history} onOpenChange={(o) => { if (!o) setHistory(null); }}>
        <DialogContent className="bg-navy-800 border-navy-700 max-w-2xl" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Edit History · {history?.date}</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-3 pr-2" data-testid="history-list">
            {(history?.edit_history || []).length === 0 && <div className="text-sm text-muted-foreground py-4">No edits recorded for this record.</div>}
            {(history?.edit_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="border border-navy-700 rounded-md p-3 text-sm" data-testid={`history-entry-${i}`}>
                <div className="flex justify-between text-[11px] uppercase tracking-widest text-muted-foreground">
                  <span>{h.name || h.user_id} <span className="gold-text">({h.role})</span></span>
                  <span>{fmtWhen(h.at)}</span>
                </div>
                <div className="mt-2 space-y-1">
                  {Object.entries(h.changes || {}).length === 0 && <div className="text-muted-foreground text-xs">No field-level diff recorded.</div>}
                  {Object.entries(h.changes || {}).map(([k, v]) => (
                    <div key={k} className="text-xs">
                      <span className="font-mono text-slate-300">{k}</span>:
                      <span className="text-rose_alert ml-1 line-through">{JSON.stringify(v.before)}</span>
                      <span className="mx-1">→</span>
                      <span className="text-emerald_on">{JSON.stringify(v.after)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Attendance</CardTitle></CardHeader>
        <CardContent className="p-5"><YesNo value={data.attendance} onChange={v => setData(d => ({ ...d, attendance: v }))} testid="tracker-attendance" /></CardContent>
      </Card>

      <Card className="bg-navy-800 border-gold/40">
        <CardHeader className="border-b border-navy-700">
          <CardTitle className="font-heading text-base">Consultation & Treatment Funnel</CardTitle>
          <p className="text-xs text-muted-foreground">Medicine-only / advice-only patients stay in Total Consultations. Treatment conversion is calculated only on patients who actually wanted treatment.</p>
        </CardHeader>
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <Counter label="Total Consultations" value={data.total_consultations} max={500} onChange={v => setData(d => ({ ...d, total_consultations: v }))} hint="All patients you saw today (any reason)." testid="ctr-total-consultations" />
          <Counter label="Patients Who Wanted Treatment" value={data.treatment_intent} max={500} onChange={v => setData(d => ({ ...d, treatment_intent: v }))} hint="Came with treatment intent / suitable & interested." testid="ctr-treatment-intent" error={errors.treatment_intent} />
          <Counter label="Patients Who Took Treatment" value={data.treatment_taken} max={500} onChange={v => setData(d => ({ ...d, treatment_taken: v }))} hint="Actually proceeded with treatment." testid="ctr-treatment-taken" error={errors.treatment_taken} />
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Treatment Not Taken (auto)</Label>
            <div className="mt-1.5 h-10 px-3 rounded-md border border-navy-700 bg-navy-900/60 grid place-items-start content-center num" data-testid="tracker-not-taken">{notTaken}</div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Treatment Conversion % (primary KPI)</Label>
            <div className="mt-1.5 h-12 px-3 rounded-md border border-gold/50 bg-navy-900/60 grid place-items-start content-center gold-text num text-lg" data-testid="tracker-conversion">
              {conversion === null ? "N/A — no treatment-intent patients" : `${conversion}%`}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Formula: Treatment Taken ÷ Treatment Intent × 100</p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Consultation → Treatment % (secondary)</Label>
            <div className="mt-1.5 h-12 px-3 rounded-md border border-navy-700 bg-navy-900/60 grid place-items-start content-center num" data-testid="tracker-cons-conversion">
              {consConversion === null ? "—" : `${consConversion}%`}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">Informational only. Not the primary conversion metric.</p>
          </div>
        </CardContent>
      </Card>

      {notTaken > 0 && (
        <Card className="bg-navy-800 border-navy-700">
          <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Why Treatment Was Not Taken? (optional)</CardTitle></CardHeader>
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Primary Reason</Label>
              <Select value={data.not_taken_reason || ""} onValueChange={v => setData(d => ({ ...d, not_taken_reason: v }))}>
                <SelectTrigger className="mt-1.5 bg-navy-800 border-navy-700" data-testid="not-taken-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                <SelectContent>{NOT_TAKEN_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {data.not_taken_reason === "Other" && (
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Other Reason</Label>
                <Input value={data.not_taken_reason_other || ""} onChange={e => setData(d => ({ ...d, not_taken_reason_other: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" data-testid="not-taken-reason-other" />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Cross-Sell / Upsell</CardTitle></CardHeader>
        <CardContent className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cross-Sell / Upsell Done?</Label>
            <div className="mt-1.5"><YesNo value={data.crosssell_done} onChange={v => setData(d => ({ ...d, crosssell_done: v }))} testid="crosssell-done" /></div>
          </div>
          {data.crosssell_done === "Y" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Procedure / Service</Label>
                <Select value={data.crosssell_procedure || ""} onValueChange={v => setData(d => ({ ...d, crosssell_procedure: v }))}>
                  <SelectTrigger className="mt-1.5 bg-navy-800 border-navy-700" data-testid="crosssell-procedure"><SelectValue placeholder="Pick" /></SelectTrigger>
                  <SelectContent>{CROSSSELL_OPTIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {data.crosssell_procedure === "Other" && (
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Custom Procedure</Label>
                  <Input value={data.crosssell_procedure_other || ""} onChange={e => setData(d => ({ ...d, crosssell_procedure_other: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" data-testid="crosssell-procedure-other" />
                </div>
              )}
              <Counter label="Cross-Sell Count" value={data.crosssell_suggested} max={50} onChange={v => setData(d => ({ ...d, crosssell_suggested: v }))} testid="ctr-crosssell-count" />
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cross-Sell Amount (₹)</Label>
                <Input type="number" value={data.crosssell_amount ?? ""} onChange={e => setData(d => ({ ...d, crosssell_amount: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700 num" data-testid="crosssell-amount" />
                <p className="text-[11px] text-muted-foreground mt-1">Only revenue-related field doctors enter.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">OT Team Training</CardTitle></CardHeader>
        <CardContent className="p-5 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">OT Team Training Given?</Label>
            <div className="mt-1.5"><YesNo value={data.ot_training} onChange={v => setData(d => ({ ...d, ot_training: v }))} testid="ot-training-yn" /></div>
          </div>
          {data.ot_training === "Y" && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Training Topic</Label>
                <Input value={data.ot_topic || ""} onChange={e => setData(d => ({ ...d, ot_topic: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" data-testid="ot-topic" />
              </div>
              <Counter label="OT Members Trained" value={data.ot_members} max={30} onChange={v => setData(d => ({ ...d, ot_members: v }))} testid="ot-members" />
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Member Names</Label>
                <Input value={data.ot_member_names || ""} onChange={e => setData(d => ({ ...d, ot_member_names: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" placeholder="Comma separated" data-testid="ot-member-names" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Remark</Label>
                <Input value={data.ot_remark || ""} onChange={e => setData(d => ({ ...d, ot_remark: e.target.value }))} className="mt-1.5 bg-navy-800 border-navy-700" data-testid="ot-remark" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isProcedureDoc && (
        <Card className="bg-navy-800 border-navy-700">
          <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Procedures / Surgeries</CardTitle></CardHeader>
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Counter label="Procedures Done" value={data.procedures_done} max={50} onChange={v => setData(d => ({ ...d, procedures_done: v }))} testid="ctr-procedures" />
            <Counter label="Surgeries Done" value={data.surgeries_done} max={20} onChange={v => setData(d => ({ ...d, surgeries_done: v }))} testid="ctr-surgeries" />
          </CardContent>
        </Card>
      )}

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Other Doctors Trained</CardTitle></CardHeader>
        <CardContent className="p-5 space-y-4">
          <Counter label="Doctors Trained Today" value={data.other_doctors_trained} max={10} onChange={v => setData(d => ({ ...d, other_doctors_trained: v }))} testid="ctr-other-doctors" />
          {trainedRows.map((t, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`trained-${i}`}>
              <Input placeholder="Doctor name" value={t.doctor_name} onChange={e => updateArr("trained_list", i, "doctor_name", e.target.value)} className="bg-navy-800 border-navy-700" />
              <Input placeholder="Topic / procedure / surgery" value={t.topic} onChange={e => updateArr("trained_list", i, "topic", e.target.value)} className="bg-navy-800 border-navy-700" />
              <Input placeholder="Remark (optional)" value={t.remark} onChange={e => updateArr("trained_list", i, "remark", e.target.value)} className="bg-navy-800 border-navy-700" />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Patient Complaints</CardTitle></CardHeader>
        <CardContent className="p-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Counter label="Major Complaints" value={data.major_complaints} max={20} onChange={v => setData(d => ({ ...d, major_complaints: v }))} testid="ctr-major-complaints" />
            <Counter label="Minor Complaints" value={data.minor_complaints} max={20} onChange={v => setData(d => ({ ...d, minor_complaints: v }))} testid="ctr-minor-complaints" />
          </div>
          {[["major_complaints_list","Major",majorList],["minor_complaints_list","Minor",minorList]].map(([key,label,list]) => list.length > 0 && (
            <div key={key} className="space-y-3 pt-3 border-t border-navy-700">
              <div className="text-xs uppercase tracking-[0.2em] gold-text">{label} — Details</div>
              {list.map((c, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-6 gap-3" data-testid={`${key}-${i}`}>
                  <div className="md:col-span-2"><Input placeholder="What happened?" value={c.text} onChange={e => updateArr(key, i, "text", e.target.value)} className="bg-navy-800 border-navy-700" /></div>
                  <div><Input placeholder="Remark" value={c.remark} onChange={e => updateArr(key, i, "remark", e.target.value)} className="bg-navy-800 border-navy-700" /></div>
                  <div><YesNo value={c.resolved} onChange={v => updateArr(key, i, "resolved", v)} testid={`${key}-resolved-${i}`} /></div>
                  <div className="md:col-span-2"><Input placeholder="Resolution remark" value={c.resolution} onChange={e => updateArr(key, i, "resolution", e.target.value)} className="bg-navy-800 border-navy-700" /></div>
                </div>
              ))}
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Extra Work / Contribution (Optional)</CardTitle></CardHeader>
        <CardContent className="p-5">
          <Textarea rows={2} value={data.extra_work || ""} onChange={e => setData(d => ({ ...d, extra_work: e.target.value }))} className="bg-navy-800 border-navy-700 resize-none" placeholder="Assisted another doctor / helped OT / prepared protocol / helped training / anything else" data-testid="extra-work" />
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Notes & Patient Feedback (Optional)</CardTitle></CardHeader>
        <CardContent className="p-5 space-y-4">
          <Textarea rows={2} value={data.notes || ""} onChange={e => setData(d => ({ ...d, notes: e.target.value }))} className="bg-navy-800 border-navy-700 resize-none" placeholder="Anything important today" data-testid="tracker-notes" />
          <Textarea rows={2} value={data.patient_feedback || ""} onChange={e => setData(d => ({ ...d, patient_feedback: e.target.value }))} className="bg-navy-800 border-navy-700 resize-none" placeholder="Feedback received personally from a patient" data-testid="tracker-feedback" />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button disabled={saving || !docId || Object.keys(errors).length > 0} onClick={save} className="gold-bg hover:opacity-90 h-11 px-8" data-testid="tracker-save-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? "Saving…" : "Save Today's Tracker"}
        </Button>
      </div>
    </div>
  );
}
